import React, {useEffect, useState, useCallback} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {
  GetBoxTypesCount,
  ImportBoxCatalogCsv,
  ClearBoxCatalog,
  summarizeBoxCatalogImport,
} from '../../actions/boxCatalogActions';
import {UpdateSettings, UpdateScanPackSettings} from '../../actions/userAction';
import {selectScanPackSettings} from '../../selectors/scanPackSelectors';
import useWidth from '../scanToCartScanning/useWidth';
import BoxCatalogView from './BoxCatalogView';

const BoxCatalogSection = ({activeTollTip, handleTollTip}) => {
  const dispatch = useDispatch();
  const windowWidth = useWidth();
  const {bothSettings} = useSelector(state => state.user);
  const gs = bothSettings?.data?.data?.general_setting;
  const scanPackSettings = useSelector(selectScanPackSettings);
  const boxScanVerification = scanPackSettings?.box_scan_verification === true;
  const [isVerificationUpdating, setIsVerificationUpdating] = useState(false);
  const [verificationError, setVerificationError] = useState(null);

  const onToggleBoxScanVerification = value => {
    if (!scanPackSettings || isVerificationUpdating) return;
    setIsVerificationUpdating(true);
    setVerificationError(null);
    dispatch(
      UpdateScanPackSettings(
        {...scanPackSettings, box_scan_verification: value},
        ({success, errorMessages} = {}) => {
          setIsVerificationUpdating(false);
          if (!success) {
            setVerificationError(
              Array.isArray(errorMessages) && errorMessages.length > 0
                ? errorMessages.join(' ')
                : 'Failed to save box scan verification.',
            );
          }
        },
      ),
    );
  };
  const [count, setCount] = useState(null);
  const [file, setFile] = useState(null);
  const [result, setResult] = useState(null);
  const [isUploading, setIsUploading] = useState(false);
  const [isClearing, setIsClearing] = useState(false);

  const refreshCount = useCallback(async () => {
    setCount(await dispatch(GetBoxTypesCount()));
  }, [dispatch]);

  useEffect(() => {
    refreshCount();
  }, [refreshCount]);

  const onUpload = useCallback(async () => {
    if (!file || isUploading) return;
    setIsUploading(true);
    const data = await dispatch(ImportBoxCatalogCsv(file));
    const summary = summarizeBoxCatalogImport(data);
    setResult(summary);
    setIsUploading(false);
    if (summary.success) {
      setFile(null);
      refreshCount();
    }
  }, [dispatch, file, isUploading, refreshCount]);

  const onClearAll = useCallback(async () => {
    setIsClearing(true);
    const data = await dispatch(ClearBoxCatalog());
    setIsClearing(false);
    if (data?.status) {
      setResult(null);
      refreshCount();
    } else {
      setResult({success: false, message: data?.error || 'Failed to clear the box catalog.', errors: []});
    }
  }, [dispatch, refreshCount]);

  const [checkEnabled, setCheckEnabled] = useState(gs?.box_champ_weight_check_enabled !== false);
  const [defaultWeight, setDefaultWeight] = useState(
    gs?.box_champ_default_weight != null ? String(gs.box_champ_default_weight) : '0',
  );

  useEffect(() => {
    setCheckEnabled(gs?.box_champ_weight_check_enabled !== false);
    setDefaultWeight(gs?.box_champ_default_weight != null ? String(gs.box_champ_default_weight) : '0');
  }, [gs?.box_champ_weight_check_enabled, gs?.box_champ_default_weight]);

  const onToggleWeight = value => {
    setCheckEnabled(value);
    dispatch(UpdateSettings({box_champ_weight_check_enabled: value}));
  };

  const onDefaultWeightChange = value => {
    const clean = String(value).replace(/[^0-9.]/g, '').replace(/(\..*)\./g, '$1');
    setDefaultWeight(clean);
  };

  const onCommitDefaultWeight = () => {
    const num = parseFloat(defaultWeight);
    dispatch(UpdateSettings({box_champ_default_weight: Number.isFinite(num) && num > 0 ? num : 0}));
  };

  const onWeightUnitChange = value => {
    dispatch(UpdateSettings({box_champ_default_weight_format: value}));
  };

  return (
    <BoxCatalogView
      count={count}
      fileName={file?.name || ''}
      result={result}
      isUploading={isUploading}
      onChooseFile={f => {
        setFile(f || null);
        setResult(null);
      }}
      onUpload={onUpload}
      onClearAll={onClearAll}
      isClearing={isClearing}
      weightCheckEnabled={checkEnabled}
      defaultWeight={defaultWeight}
      weightUnit={gs?.box_champ_default_weight_format || 'oz'}
      windowWidth={windowWidth}
      onToggleWeight={onToggleWeight}
      boxScanVerification={boxScanVerification}
      isBoxScanVerificationUpdating={isVerificationUpdating}
      boxScanVerificationError={verificationError}
      onToggleBoxScanVerification={onToggleBoxScanVerification}
      onDefaultWeightChange={onDefaultWeightChange}
      onCommitDefaultWeight={onCommitDefaultWeight}
      onWeightUnitChange={onWeightUnitChange}
      activeTollTip={activeTollTip}
      handleTollTip={handleTollTip}
    />
  );
};

export default BoxCatalogSection;
